import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Users, X } from "lucide-react";
import type { DailyEntryOptions } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AddCrewModalProps {
  open: boolean;
  onClose: () => void;
  options: DailyEntryOptions;
  existingWorkerIds: string[];
  onApply: (crewId: string, defaultHours: number) => void;
}

export function AddCrewModal({
  open,
  onClose,
  options,
  existingWorkerIds,
  onApply,
}: AddCrewModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [crewId, setCrewId] = useState("");
  const [defaultHours, setDefaultHours] = useState(8);

  const selectedCrew = options.crews.find((c) => c.id === crewId);
  const newMembers =
    selectedCrew?.members.filter((m) => !existingWorkerIds.includes(m.workerId)) ?? [];
  const skipped = (selectedCrew?.members.length ?? 0) - newMembers.length;
  const inactive = newMembers.filter((m) => m.workerStatus === "INACTIVE");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!crewId && options.crews[0]) setCrewId(options.crews[0].id);
  }, [open, options.crews, crewId]);

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto w-full max-w-lg rounded-xl border-0 bg-card-white p-0 shadow-[var(--shadow-elevated)] backdrop:bg-navy-deep/50"
      onClose={onClose}
    >
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy-deep">
              <Users className="h-5 w-5" />
              Add crew to board
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Applies defaults and skips workers already on the board.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Crew</Label>
            <select
              value={crewId}
              onChange={(e) => { setCrewId(e.target.value); }}
              className="flex h-10 w-full rounded-md border border-input px-3 text-sm"
            >
              {options.crews.map((crew) => (
                <option key={crew.id} value={crew.id}>
                  {crew.name} ({crew.members.length} workers)
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Default hours</Label>
            <Input
              type="number"
              min={0}
              max={24}
              step={0.5}
              value={defaultHours}
              onChange={(e) => { setDefaultHours(Number(e.target.value) || 0); }}
              className="h-11 text-lg font-semibold"
            />
          </div>

          {selectedCrew ? (
            <div className="rounded-lg bg-bg-light px-3 py-2 text-sm">
              <p>
                Adding <strong>{newMembers.length}</strong> worker{newMembers.length === 1 ? "" : "s"}
                {skipped > 0 ? ` · skipping ${skipped} duplicate${skipped === 1 ? "" : "s"}` : ""}
              </p>
              {inactive.length > 0 ? (
                <p className="mt-1 flex items-center gap-1 text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {inactive.length} inactive worker{inactive.length === 1 ? "" : "s"} will be flagged
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (crewId) onApply(crewId, defaultHours);
              onClose();
            }}
            disabled={!crewId || newMembers.length === 0}
          >
            Add crew
          </Button>
        </div>
      </div>
    </dialog>
  );
}
